import { Chart, type Plugin } from 'chart.js';
import { callback, getRelativePosition } from 'chart.js/helpers';
const chartStates = new WeakMap();

function getState(chart: DragBoxPluginChart) {
  let state = chartStates.get(chart);
  if (!state) {
    state = {
      handlers: {},
    };
    chartStates.set(chart, state);
  }
  return state;
}
function removeState(chart: DragBoxPluginChart) {
  chartStates.delete(chart);
}
function removeHandler(chart: DragBoxPluginChart, type: string) {
  const { handlers } = getState(chart);
  const handler = handlers[type];
  if (handler && handler.target) {
    handler.target.removeEventListener(type, handler);
    delete handlers[type];
  }
}

function addHandler(
  chart: DragBoxPluginChart,
  target: any,
  type: string,
  handler: (chart: DragBoxPluginChart, event: Event, options: any) => void,
) {
  const { handlers, options } = getState(chart);
  const oldHandler = handlers[type];
  if (oldHandler && oldHandler.target === target) {
    // already attached
    return;
  }
  removeHandler(chart, type);
  handlers[type] = (event: Event) => handler(chart, event, options);
  handlers[type].target = target;

  target.addEventListener(type, handlers[type]);
}
function addListeners(chart: DragBoxPluginChart) {
  const canvas = chart.canvas;

  addHandler(chart, canvas, 'mousedown', mouseDown);
  addHandler(chart, canvas.ownerDocument, 'mouseup', mouseUp);
}
function removeListeners(chart: DragBoxPluginChart) {
  removeHandler(chart, 'mousedown');
  removeHandler(chart, 'mousemove');
  removeHandler(chart, 'mouseup');
}
function mouseMove(chart: DragBoxPluginChart, event: Event, options: any) {
  const state = getState(chart);
  if (state.dragStart) {
    state.dragging = true;
    state.dragEnd = event;
    state.endPoint = computePoint(chart, event);

    chart.update('none');
  }
}

function mouseDown(chart: DragBoxPluginChart, event: Event, options: any) {
  if (!chart.canvas) return;
  const state = getState(chart);
  state.dragging = true;

  // Remove drag start and end before chart update to stop drawing selected area
  state.dragEnd = event;
  state.dragStart = event;
  state.beginPoint = computePoint(chart, event);
  state.endPoint = state.beginPoint;
  addHandler(chart, chart.canvas, 'mousemove', mouseMove);
}

function mouseUp(chart: DragBoxPluginChart) {
  const state = getState(chart);
  if (!state.dragStart) {
    return;
  }

  removeHandler(chart, 'mousemove');
  state.dragging = false;
  onComplete(chart);
  chart.update('none');
  return;
}

function computePoint(chart: DragBoxPluginChart, event: Event): number {
  const point = getRelativePosition(event, chart as any);
  const {
    scales: { x },
  } = chart;
  const l = x.getValueForPixel(point.x) ?? 0;
  return l;
}
function computeDragRect(
  chart: DragBoxPluginChart,
  beginX: number,
  endX: number,
) {
  let { left, right } = chart.chartArea;
  const { top, bottom } = chart.chartArea;
  const {
    scales: { x },
  } = chart;

  const _left = x.getPixelForValue(beginX); //- 10
  const _right = x.getPixelForValue(endX); //+ 10

  left = Math.min(_left, _right);
  right = Math.max(_left, _right);

  const width = right - left;
  const height = bottom - top;

  return {
    left,
    top,
    right,
    bottom,
    width,
    height,
  };
}
function computeDraggingRect(
  chart: DragBoxPluginChart,
  beginPointEvent: Event,
  endPointEvent: Event,
) {
  let { left, right } = chart.chartArea;
  const { top, bottom } = chart.chartArea;
  const beginPoint = getRelativePosition(beginPointEvent, chart as any);
  const endPoint = getRelativePosition(endPointEvent, chart as any);

  left = Math.min(beginPoint.x, endPoint.x);
  right = Math.max(beginPoint.x, endPoint.x);

  const width = right - left;
  const height = bottom - top;

  return {
    left,
    top,
    right,
    bottom,
    width,
    height,
  };
}
function draw(chart: DragBoxPluginChart, caller: string, options: any) {
  const dragOptions: any = options;
  const { beginPoint, endPoint, dragStart, dragEnd, dragging } =
    getState(chart);
  if (dragOptions.drawTime !== caller || !endPoint) {
    return;
  }
  const box = dragging
    ? computeDraggingRect(chart, dragStart, dragEnd)
    : computeDragRect(chart, beginPoint, endPoint);
  const { left, top, width, height } = box;
  const ctx = chart.ctx;

  ctx.save();
  ctx.beginPath();
  ctx.fillStyle = dragOptions.backgroundColor || 'rgba(225,225,225,0.3)';

  ctx.fillRect(left, top, width, height);

  if (dragOptions.borderWidth > 0) {
    ctx.lineWidth = dragOptions.borderWidth;
    ctx.strokeStyle = dragOptions.borderColor || 'rgba(225,225,225)';
    ctx.strokeRect(left, top, width, height);
  }
  ctx.restore();
}

function onComplete(chart: DragBoxPluginChart) {
  const { options } = getState(chart);
  if (options) {
    callback(options.onComplete, [{ chart }]);
  }
}
function getRange(chart: DragBoxPluginChart) {
  const { beginPoint, endPoint } = getState(chart);
  if (beginPoint !== null && endPoint !== null) {
    const startX = Math.min(beginPoint, endPoint);
    const endX = Math.max(beginPoint, endPoint);

    return { startX, endX };
  } else {
    return undefined;
  }
}
function resetRange(chart: DragBoxPluginChart) {
  const state = getState(chart);

  state.beginPoint = null;
  state.endPoint = null;
  state.dragStart = null;
  state.dragEnd = null;
  onComplete(chart);
  chart.update('none');
}
export interface DragBoxPluginChart extends Chart {
  getRange: () => { startX: number; endX: number } | undefined;
  resetRange: () => void;
}
export const DragBoxPlugin: Plugin = {
  id: 'drawBox',

  defaults: {
    drawTime: 'beforeDatasetsDraw',
    mode: 'x',
  },
  start: function (chart: DragBoxPluginChart) {
    chart.getRange = () => getRange(chart);
    chart.resetRange = () => resetRange(chart);
  },

  beforeEvent(chart: DragBoxPluginChart) {
    const state = getState(chart);

    if (state.dragging) {
      // cancel any event handling while dragging
      return false;
    }
  },

  beforeUpdate: function (chart: DragBoxPluginChart, args, options) {
    const state = getState(chart);
    state.options = options;
    addListeners(chart);
  },

  beforeDatasetsDraw(chart: DragBoxPluginChart, _args, options) {
    draw(chart, 'beforeDatasetsDraw', options);
  },

  afterDatasetsDraw(chart: DragBoxPluginChart, _args, options) {
    draw(chart, 'afterDatasetsDraw', options);
  },

  beforeDraw(chart: DragBoxPluginChart, _args, options) {
    draw(chart, 'beforeDraw', options);
  },

  afterDraw(chart: DragBoxPluginChart, _args, options) {
    draw(chart, 'afterDraw', options);
  },
  stop: function (chart: DragBoxPluginChart) {
    removeListeners(chart);
    removeState(chart);
  },
};
